import React, { useMemo } from 'react';
import { Col, Row } from '../../../lib/utils';
import { colors } from '../../../lib/colors';
import { CaptionLight, Subtitle1Bold, Body2Regular } from '../../../lib/font';
import { useCenterSubscriptions } from '@/features/facilitySubscription/hooks';
import { IFacilitySubscription } from '@/features/facilitySubscription/types';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const STATUS_LABEL: Record<string, string> = {
    active: '이용 중',
    expired: '만료',
    refunded: '환불',
};

const STATUS_COLORS: Record<string, string> = {
    active: '#10B981',
    expired: '#9CA3AF',
    refunded: '#EF4444',
};

const CATEGORY_LABEL: Record<string, string> = {
    gym: '헬스(GYM)',
    locker: '락커',
    clothing: '운동복',
    etc: '기타',
};

const SubscriptionStatsTab = ({ centerId }: { centerId: string }) => {
    // 전체 이용권 조회
    const { data: subData, isLoading } = useCenterSubscriptions({
        centerId,
        status: 'all',
        limit: 9999,
    });

    const subscriptions: IFacilitySubscription[] = subData?.results ?? [];

    // 상태별 집계
    const statusCount = useMemo(() => {
        const map: Record<string, number> = {};
        subscriptions.forEach((s) => {
            map[s.status] = (map[s.status] || 0) + 1;
        });
        return map;
    }, [subscriptions]);

    const statusPieData = Object.entries(statusCount).map(([key, count]) => ({
        name: STATUS_LABEL[key] || key,
        value: count,
        color: STATUS_COLORS[key] || '#9CA3AF',
    }));

    // 카테고리별 매출 집계
    const categoryRevenue = useMemo(() => {
        const map: Record<string, { count: number; revenue: number }> = {};
        subscriptions.forEach((s) => {
            const cat = s.category || 'etc';
            if (!map[cat]) map[cat] = { count: 0, revenue: 0 };
            map[cat].count++;
            map[cat].revenue += s.price || 0;
        });
        return map;
    }, [subscriptions]);

    const categoryBarData = Object.entries(categoryRevenue).map(([key, val]) => ({
        category: CATEGORY_LABEL[key] || key,
        revenue: val.revenue,
        count: val.count,
    }));

    // 상품별 집계
    const productRevenue = useMemo(() => {
        const map: Record<string, { count: number; revenue: number; category: string }> = {};
        subscriptions.forEach((s) => {
            const name = s.productName || '기타';
            if (!map[name]) map[name] = { count: 0, revenue: 0, category: s.category || 'etc' };
            map[name].count++;
            map[name].revenue += s.price || 0;
        });
        return Object.entries(map)
            .map(([name, val]) => ({ name, ...val }))
            .sort((a, b) => b.revenue - a.revenue);
    }, [subscriptions]);

    const productPieData = productRevenue.map((p) => ({
        name: p.name,
        value: p.revenue,
    }));

    const totalRevenue = subscriptions.reduce((sum, s) => sum + (s.price || 0), 0);
    const activeCount = statusCount['active'] || 0;
    const refundedCount = statusCount['refunded'] || 0;

    if (isLoading) {
        return (
            <Col style={{ alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
                <Body2Regular color={colors.LAVEL_1}>데이터를 불러오는 중...</Body2Regular>
            </Col>
        );
    }

    return (
        <Col>
            {/* KPI 카드 */}
            <Row style={{ marginTop: 16, gap: 16 }}>
                <KpiCard label="총 이용권" value={subscriptions.length} unit="건" bgColor="#EFF6FF" textColor="#2563EB" />
                <KpiCard label="이용 중" value={activeCount} unit="건" bgColor="#F0FDF4" textColor="#16A34A" />
                <KpiCard label="총 매출" value={`${totalRevenue.toLocaleString()}원`} bgColor="#FFF7ED" textColor="#EA580C" />
                <KpiCard label="환불" value={refundedCount} unit="건" bgColor="#FEF2F2" textColor="#DC2626" />
            </Row>

            {/* 차트 영역 */}
            <Row style={{ marginTop: 32, gap: 24, alignItems: 'flex-start' }}>
                {/* 상태별 파이 차트 */}
                <Col className="flex-1">
                    <Body2Regular color={colors.LAVEL_2} className="mb-[12px]">
                        이용권 상태별 분포
                    </Body2Regular>
                    {statusPieData.length > 0 ? (
                        <Col className="items-center">
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={statusPieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={55}
                                        outerRadius={95}
                                        paddingAngle={3}
                                        dataKey="value"
                                    >
                                        {statusPieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                                        formatter={(value: any, name: any) => [`${Number(value).toLocaleString()}건`, name]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <Row style={{ gap: 24, marginTop: -8 }}>
                                {statusPieData.map((d) => (
                                    <Row key={d.name} style={{ alignItems: 'center', gap: 6 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: d.color }} />
                                        <CaptionLight color={colors.LAVEL_2}>
                                            {d.name}: {d.value.toLocaleString()}건
                                        </CaptionLight>
                                    </Row>
                                ))}
                            </Row>
                        </Col>
                    ) : (
                        <EmptyState />
                    )}
                </Col>

                {/* 상품별 매출 파이 차트 */}
                <Col className="flex-1">
                    <Body2Regular color={colors.LAVEL_2} className="mb-[12px]">
                        상품별 매출 비율
                    </Body2Regular>
                    {productPieData.length > 0 ? (
                        <Col className="items-center">
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={productPieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }: any) =>
                                            `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                                        }
                                        outerRadius={95}
                                        dataKey="value"
                                    >
                                        {productPieData.map((_, index) => (
                                            <Cell key={`pie-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                                        formatter={(value: any) => [`${Number(value).toLocaleString()}원`, '매출']}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </Col>
                    ) : (
                        <EmptyState />
                    )}
                </Col>
            </Row>

            {/* 카테고리별 매출 바 차트 */}
            {categoryBarData.length > 0 && (
                <Col style={{ marginTop: 32 }}>
                    <Body2Regular color={colors.LAVEL_2} className="mb-[12px]">
                        카테고리별 매출
                    </Body2Regular>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={categoryBarData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                            <XAxis dataKey="category" tick={{ fontSize: 13, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} />
                            <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} tickFormatter={(v) => `${(v / 10000).toFixed(0)}만`} />
                            <Tooltip
                                contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                                formatter={(value: any, name: any) => [
                                    name === 'revenue' ? `${Number(value).toLocaleString()}원` : `${value}건`,
                                    name === 'revenue' ? '매출' : '판매 수',
                                ]}
                            />
                            <Bar dataKey="revenue" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={50} />
                        </BarChart>
                    </ResponsiveContainer>
                </Col>
            )}

            {/* 상품별 상세 테이블 */}
            {productRevenue.length > 0 && (
                <Col style={{ marginTop: 32 }}>
                    <Body2Regular color={colors.LAVEL_2} className="mb-[12px]">
                        상품별 매출 상세
                    </Body2Regular>
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ backgroundColor: '#F9FAFB' }}>
                                    <th style={thStyle}>상품명</th>
                                    <th style={thStyle}>카테고리</th>
                                    <th style={thStyle}>판매 수</th>
                                    <th style={thStyle}>총 매출</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productRevenue.map((p) => (
                                    <tr key={p.name} style={{ borderTop: '1px solid #F3F4F6' }}>
                                        <td style={tdStyle}>{p.name}</td>
                                        <td style={tdStyle}>{CATEGORY_LABEL[p.category] || p.category}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{p.count}건</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>
                                            {p.revenue.toLocaleString()}원
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Col>
            )}
        </Col>
    );
};

const thStyle: React.CSSProperties = {
    padding: '10px 16px',
    textAlign: 'left',
    fontWeight: 500,
    color: '#6B7280',
    fontSize: 13,
};

const tdStyle: React.CSSProperties = {
    padding: '10px 16px',
    color: '#374151',
    fontSize: 13,
};

const KpiCard = ({ label, value, unit, bgColor, textColor }: {
    label: string; value: number | string; unit?: string; bgColor: string; textColor: string;
}) => (
    <Col className="flex-1" style={{ backgroundColor: bgColor, borderRadius: 12, padding: 20 }}>
        <CaptionLight color={colors.LAVEL_1}>{label}</CaptionLight>
        <Subtitle1Bold className="mt-[4px]" style={{ color: textColor }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
            {unit && <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 2 }}>{unit}</span>}
        </Subtitle1Bold>
    </Col>
);

const EmptyState = () => (
    <Col style={{ alignItems: 'center', justifyContent: 'center', padding: '60px 0', backgroundColor: '#F8F9FA', borderRadius: 12 }}>
        <Body2Regular color={colors.LAVEL_1}>데이터가 없습니다</Body2Regular>
        <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">이용권을 등록하면 통계가 표시됩니다</CaptionLight>
    </Col>
);

export default SubscriptionStatsTab;
